import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Checkbox, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';

import { useCalories } from 'src/hooks/useCalories';
import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import {
    getModifierProductsString,
    getModifierProductsTotal,
    getProductOptionsDetails
} from 'components/menu/model/Menu';
import { addOpacity, getCurrencyString, showCalories } from 'lib/helpers';
import { RecursiveModifierProductList } from 'components/bill/ui/ViewBill/RecursiveModifierProducts';
import { isEmptyString } from 'lib/typeInference';

const PREFIX = 'RecentOrderItemBox';

const classes = {
    opaqueText: `${PREFIX}opaqueText`,
    containerBox: `${PREFIX}containerBox`
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'showBorder'
})<{ showBorder: boolean }>(({ theme, showBorder }) => ({
    ...(showBorder && {
        borderBottom: `1px solid ${theme.palette.divider}`
    }),
    [`& .${classes.opaqueText}`]: {
        color: addOpacity(theme.palette.text.primary, 0.6)
    }
}));

interface IRecentOrderItemBoxProps {
    selected: boolean;
    onChange: (id: number) => void;
    item: IItemReadResourceV10;
    modifierItems: IItemReadResourceV10[];
    currencyCode: string;
    isLast: boolean;
}

export const RecentOrderItemBox: React.FC<React.PropsWithChildren<IRecentOrderItemBoxProps>> = ({
    selected,
    onChange,
    item,
    item: { quantity, cost, productName, modifiers, id },
    modifierItems,
    currencyCode,
    isLast
}) => {
    const { getCaloriesString, getOrderItemsCalories } = useCalories();
    const { t } = useTranslation();

    const hasNestedModifiers = React.useMemo(
        () => modifierItems.some(item => item.children && item.children.length > 0),
        [modifierItems]
    );
    const [showMore, setShowMore] = React.useState(false);

    const modifierProductsTotal = React.useMemo(
        () => getModifierProductsTotal(modifierItems, quantity) + cost,
        [modifierItems, quantity, cost]
    );

    const modifierProductsString = React.useMemo(() => {
        const string = getModifierProductsString(modifierItems, quantity);
        return string.length > 100 && hasNestedModifiers ? string.slice(0, 100) + '...' : string;
    }, [modifierItems, quantity, hasNestedModifiers]);

    const [modifierOptionsString, calculatedOptionsPrice] = React.useMemo(
        () => getProductOptionsDetails(modifiers, false),
        [modifiers]
    );

    const hasModifierInfo = React.useMemo(
        () => !isEmptyString(modifierProductsString) || !isEmptyString(modifierOptionsString),
        [modifierProductsString, modifierOptionsString]
    );

    const totalPrice = React.useMemo(
        () => Math.round((modifierProductsTotal + calculatedOptionsPrice) * quantity * 100) / 100,
        [modifierProductsTotal, calculatedOptionsPrice, quantity]
    );

    const handleChange = React.useCallback(() => {
        onChange(id);
    }, [id, onChange]);
    const totalItemCalories = React.useMemo(
        () => getOrderItemsCalories([item]),
        [getOrderItemsCalories, item]
    );

    const priceString = React.useMemo(() => {
        if (showCalories(totalItemCalories)) {
            return `${getCurrencyString(totalPrice, currencyCode)} | ${getCaloriesString(
                totalItemCalories.value
            )}`;
        }

        return getCurrencyString(totalPrice, currencyCode);
    }, [currencyCode, getCaloriesString, totalPrice, totalItemCalories]);

    const handleShowMore = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();
            setShowMore(!showMore);
        },
        [showMore]
    );

    return (
        <StyledBox onClick={handleChange} showBorder={!isLast}>
            <Grid container alignItems="center">
                <Grid size={10}>
                    <Typography variant="h6">
                        {quantity}
                        {'x'} {productName}
                    </Typography>
                </Grid>
                <Grid size={2}>
                    <Box display="flex" justifyContent="flex-end">
                        <Checkbox checked={selected} />
                    </Box>
                </Grid>
            </Grid>
            {hasModifierInfo && (
                <Box display="inline" marginBottom={1}>
                    {!isEmptyString(modifierOptionsString) && (
                        <Typography color="textPrimary" className={classes.opaqueText}>
                            {modifierOptionsString}
                        </Typography>
                    )}
                    {!showMore && (
                        <Typography color="textPrimary" display="inline" className={classes.opaqueText}>
                            {modifierProductsString}
                        </Typography>
                    )}
                    {showMore && (
                        <RecursiveModifierProductList
                            modifierProducts={modifierItems}
                            orderItemQuantity={quantity}
                            typographyVariant="body1"
                        />
                    )}
                    {hasNestedModifiers && (
                        <IconButton
                            sx={{ paddingY: 0, marginRight: 0.75, height: 'fit-content' }}
                            size="small"
                            color="primary"
                            onClick={handleShowMore}
                        >
                            {showMore ? (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <KeyboardArrowUpIcon fontSize="small" />{' '}
                                    <Typography className={classes.opaqueText}>
                                        {t('NESTED_MODIFIERS_SHOW_LESS')}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <KeyboardArrowDownIcon fontSize="small" />{' '}
                                    <Typography className={classes.opaqueText}>
                                        {t('NESTED_MODIFIERS_SHOW_MORE')}
                                    </Typography>
                                </Box>
                            )}
                        </IconButton>
                    )}
                </Box>
            )}
            <Box marginTop={0.5}>
                <Typography>{priceString}</Typography>
            </Box>
        </StyledBox>
    );
};
